import { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../../components/layout/Layout';
import { api } from '../../api/api';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import { Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, Box, TextField, Autocomplete, CircularProgress } from '@mui/material';
import './requests.css';

const REQUEST_TYPES = [
  { value: 'price_change', label: '가격 변경' },
  { value: 'size_change', label: '사이즈 변경' },
  { value: 'menu_update', label: '메뉴 수정' },
  { value: 'new_menu', label: '신메뉴 추가' },
  { value: 'remove_menu', label: '메뉴 삭제' },
  { value: 'other', label: '기타' },
];

const M_SIZE_KEYS = ['hot_s', 'hot_m', 'ice_s', 'ice_m'];
const M_SIZE_LABELS = { hot_s: 'HOT 12oz', hot_m: 'HOT 16oz', ice_s: 'ICED 16oz', ice_m: 'ICED 20oz' };
const M_SIZE_SHORT = { hot_s: '12oz', hot_m: '16oz', ice_s: '16oz', ice_m: '20oz' };
const M_CATS = [
  { key: 'espresso', label: 'ESPRESSO' },
  { key: 'brewed_coffee', label: 'BREWED' },
  { key: 'tea', label: 'TEA' },
  { key: 'cold_drink', label: 'COLD DRINKS' },
  { key: 'chocolate', label: 'CHOCOLATE' },
];
const mThS = { padding: '10px 12px', textAlign: 'center', fontWeight: 600, fontSize: 11, letterSpacing: 0.5 };
const mThS2 = { padding: '6px 8px', textAlign: 'center', fontSize: 10, color: '#888', fontWeight: 600, background: '#f9f9f9' };
const mFmt = (cents) => (cents / 100).toFixed(2);

// SLA 표시 (서버 services/requestSla.js 가 목록에 sla 를 붙여 줌) — 우선순위 · 응답/해결 남은 시간
const SLA_COLORS = { ok: ['#475569', '#f1f5f9'], warn: ['#b45309', '#fef3c7'], breach: ['#b91c1c', '#fee2e2'] };
function fmtRemain(mins) {
  const m = Math.abs(mins);
  const t = m < 60 ? `${m}분` : m < 60 * 48 ? `${Math.round(m / 60)}시간` : `${Math.round(m / 1440)}일`;
  return mins >= 0 ? `${t} 남음` : `${t} 지남`;
}
function SlaChip({ sla }) {
  if (!sla || sla.phase === 'done') return sla?.priority ? <span style={{ fontSize: 11, color: '#94a3b8' }}>{sla.priority}</span> : null;
  const [fg, bg] = SLA_COLORS[sla.level] || SLA_COLORS.ok;
  const label = `${sla.priority || ''} · ${sla.phase === 'respond' ? '응답' : '해결'} ${fmtRemain(sla.remainingMins)}`;
  return <span title={sla.deadline ? new Date(sla.deadline).toLocaleString() : ''} style={{ fontSize: 11, fontWeight: 600, color: fg, background: bg, padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>{label}</span>;
}

function ManualRequestModal({ open, onClose, onSubmit, userId, currentUser, designers }) {
  const [stores, setStores] = useState([]);
  const [storeSearch, setStoreSearch] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);
  const [menuPrices, setMenuPrices] = useState([]);
  const [menuMasters, setMenuMasters] = useState([]);
  const [priceEdits, setPriceEdits] = useState({});
  const [resetKey, setResetKey] = useState(0);
  const [activeCat, setActiveCat] = useState('espresso');
  const [type, setType] = useState('price_change');
  const [changeDetails, setChangeDetails] = useState('');
  const [loadingStores, setLoadingStores] = useState(false);
  const [loadingMenus, setLoadingMenus] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requesterName, setRequesterName] = useState('');
  const [requesterContact, setRequesterContact] = useState('');
  const [contactChannel, setContactChannel] = useState('phone');
  const [assignDesignerId, setAssignDesignerId] = useState('');

  const currentUserRole = currentUser?.role || currentUser?.user?.role;
  const currentUserName = currentUser?.username || currentUser?.user?.username || '';
  const currentSubUserId = currentUser?.user?._id || currentUser?._id;
  const isDesigner = currentUserRole === 'designer' || currentUserRole === 'user';

  // 스토어 목록 로드
  useEffect(() => {
    if (!open || !userId) return;
    setLoadingStores(true);
    api.get(`/store/getall/${userId}`).then(r => {
      setStores(r.data?.stores || r.data || []);
    }).catch(() => {}).finally(() => setLoadingStores(false));
  }, [open, userId]);

  // 스토어 선택 시 메뉴 + 마스터 로드
  useEffect(() => {
    if (!selectedStore) { setMenuPrices([]); setMenuMasters([]); setPriceEdits({}); return; }
    setLoadingMenus(true);
    Promise.all([
      api.get(`/menu-prices/${selectedStore._id}`),
      api.get('/menu-master/items').catch(() => ({ data: { items: [] } })),
    ]).then(([pr, mm]) => {
      setMenuPrices(pr.data || []);
      setMenuMasters(mm.data?.items || mm.data || []);
    }).catch(() => {}).finally(() => setLoadingMenus(false));
  }, [selectedStore]);

  const handleEdit = (menuCode, sizeKey, value) => {
    const cents = Math.round(parseFloat(value || 0) * 100);
    setPriceEdits(prev => ({ ...prev, [menuCode]: { ...(prev[menuCode] || {}), [sizeKey]: cents } }));
  };

  // 변경 요약 계산
  const changeSummary = useMemo(() => {
    return Object.entries(priceEdits).flatMap(([code, sizes]) => {
      const mp = menuPrices.find(m => m.menuCode === code);
      if (!mp) return [];
      return Object.entries(sizes).filter(([k, v]) => v !== (mp.prices?.[k] || 0) && v > 0)
        .map(([k, v]) => ({ menuCode: code, menuName: mp.menuName, sizeKey: k, oldPrice: mp.prices?.[k] || 0, newPrice: v }));
    });
  }, [priceEdits, menuPrices]);

  // 카테고리별 메뉴
  const catMenus = useMemo(() => {
    return menuPrices.filter(mp => {
      const m = menuMasters.find(mm => mm.code === mp.menuCode);
      return m?.category === activeCat;
    }).sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
  }, [menuPrices, menuMasters, activeCat]);

  const catCounts = useMemo(() => M_CATS.map(c => ({
    ...c,
    count: menuPrices.filter(mp => menuMasters.find(m => m.code === mp.menuCode)?.category === c.key).length,
  })), [menuPrices, menuMasters]);

  const reset = () => {
    setSelectedStore(null); setType('price_change');
    setChangeDetails(''); setPriceEdits({}); setRequesterName('');
    setRequesterContact(''); setContactChannel('phone'); setStoreSearch('');
    setAssignDesignerId(''); setActiveCat('espresso');
    setMenuPrices([]); setMenuMasters([]);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    if (!selectedStore) return alert('스토어를 선택해주세요.');
    if (type === 'price_change' && changeSummary.length === 0 && !changeDetails.trim()) {
      return alert('가격을 수정하거나 변경 상세 내용을 입력해주세요.');
    }
    if (type !== 'price_change' && !changeDetails.trim()) {
      return alert('변경 상세 내용을 입력해주세요.');
    }

    setSubmitting(true);
    try {
      const contactInfo = requesterName
        ? `[${contactChannel === 'phone' ? '전화' : contactChannel === 'email' ? '이메일' : '메세지'}] ${requesterName}${requesterContact ? ` (${requesterContact})` : ''}`
        : '';

      // 디자이너 배정 결정
      let assignedTo = null;
      let assignedToName = '';
      if (isDesigner) {
        assignedTo = currentSubUserId;
        assignedToName = currentUserName;
      } else if (assignDesignerId) {
        const d = (designers || []).find(x => x._id === assignDesignerId);
        if (d) { assignedTo = d._id; assignedToName = d.username; }
      }

      // 가격 변경 상세 자동 생성
      let autoDetails = '';
      if (type === 'price_change' && changeSummary.length > 0) {
        autoDetails = `가격 변경 요청 (${changeSummary.length}건):\n` +
          changeSummary.map(c =>
            `${c.menuName} ${M_SIZE_LABELS[c.sizeKey]}: $${mFmt(c.oldPrice)} → $${mFmt(c.newPrice)}`
          ).join('\n');
      }

      const fullDetails = [contactInfo, autoDetails, changeDetails].filter(Boolean).join('\n');

      const data = {
        store: selectedStore._id,
        user: userId,
        type,
        changeDetails: fullDetails,
        priceChanges: changeSummary,
        requestedByName: requesterName || `${currentUserName} (수동입력)`,
        registeredByName: currentUserName,
        registeredByRole: currentUserRole,
        ...(assignedTo && { assignedTo, assignedToName }),
      };

      await onSubmit(data);
      handleClose();
    } catch (e) {
      console.error(e);
      alert('요청 등록 실패: ' + (e.message || ''));
    }
    setSubmitting(false);
  };

  const filteredStores = useMemo(() => {
    if (!storeSearch) return stores;
    const q = storeSearch.toLowerCase();
    return stores.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.tljNo || '').toLowerCase().includes(q) ||
      (s.town || '').toLowerCase().includes(q)
    );
  }, [stores, storeSearch]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ borderBottom: '3px solid #551FFF', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
        <AddCircleOutlineIcon style={{ color: '#551FFF' }} />
        메뉴얼 입력 — 가격 수정 요청
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* 고객 연락처 */}
          <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#551FFF', marginBottom: 8 }}>고객 정보 (선택)</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select value={contactChannel} onChange={e => setContactChannel(e.target.value)}
                style={{ fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', minWidth: 80 }}>
                <option value="phone">전화</option>
                <option value="email">이메일</option>
                <option value="message">메세지</option>
              </select>
              <TextField size="small" placeholder="요청자 이름" value={requesterName}
                onChange={e => setRequesterName(e.target.value)} sx={{ flex: 1, minWidth: 100 }} />
              <TextField size="small" placeholder="연락처" value={requesterContact}
                onChange={e => setRequesterContact(e.target.value)} sx={{ flex: 1, minWidth: 120 }} />
            </div>
          </div>

          {/* 상단: 스토어 + 타입 + 디자이너 한 줄 */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {/* 스토어 검색 */}
            <Autocomplete
              options={filteredStores}
              getOptionLabel={(opt) => `${opt.name}${opt.tljNo ? ` (${opt.tljNo})` : ''}${opt.town ? ` — ${opt.town}` : ''}`}
              value={selectedStore}
              onChange={(_, val) => { setSelectedStore(val); setPriceEdits({}); setActiveCat('espresso'); }}
              inputValue={storeSearch}
              onInputChange={(_, val) => setStoreSearch(val)}
              loading={loadingStores}
              sx={{ flex: 2, minWidth: 200 }}
              renderInput={(params) => (
                <TextField {...params} label="스토어 검색" placeholder="이름, 번호, 지역..."
                  InputProps={{ ...params.InputProps, startAdornment: <SearchIcon style={{ color: '#9ca3af', marginRight: 4 }} />, endAdornment: (<>{loadingStores ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>) }} />
              )}
              noOptionsText="검색 결과 없음"
              size="small"
            />

            {/* 요청 타입 */}
            <FormControl size="small" sx={{ flex: 1, minWidth: 130 }}>
              <InputLabel>요청 타입</InputLabel>
              <Select label="요청 타입" value={type} onChange={e => setType(e.target.value)}>
                {REQUEST_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {/* 디자이너 배정 */}
          {!isDesigner && (designers || []).length > 0 && (
            <FormControl size="small" fullWidth>
              <InputLabel>디자이너 배정 (선택)</InputLabel>
              <Select label="디자이너 배정 (선택)" value={assignDesignerId} onChange={e => setAssignDesignerId(e.target.value)}>
                <MenuItem value="">미배정</MenuItem>
                {(designers || []).map(d => <MenuItem key={d._id} value={d._id}>{d.username}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {isDesigner && (
            <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
              <PersonIcon style={{ fontSize: 14, verticalAlign: 'text-bottom', marginRight: 4 }} />
              담당 디자이너: {currentUserName} (자동 배정)
            </div>
          )}

          {/* ===== 메뉴 가격 테이블 (스토어 선택 + price_change 타입) ===== */}
          {selectedStore && type === 'price_change' && (
            loadingMenus ? (
              <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
            ) : menuPrices.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#999', padding: 20, fontSize: 13 }}>이 스토어에 등록된 메뉴가 없습니다.</div>
            ) : (
              <Box>
                {/* Category pills */}
                <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}>
                  {catCounts.map(c => (
                    <Box key={c.key} onClick={() => setActiveCat(c.key)} sx={{
                      flexShrink: 0, px: 1.5, py: 0.6, borderRadius: '16px', fontSize: 11, fontWeight: 600,
                      cursor: 'pointer', background: activeCat === c.key ? '#1B5E3B' : '#f5f5f5',
                      color: activeCat === c.key ? '#fff' : '#757575', transition: '0.15s',
                    }}>{c.label} ({c.count})</Box>
                  ))}
                </Box>

                {/* Price Table */}
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden', maxHeight: 400, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: '#1B5E3B', color: '#fff' }}>
                        <th style={{ ...mThS, textAlign: 'left', minWidth: 140 }}>Product</th>
                        <th colSpan={2} style={{ ...mThS, background: '#C62828' }}>HOT</th>
                        <th colSpan={2} style={{ ...mThS, background: '#1565C0' }}>ICED</th>
                      </tr>
                      <tr style={{ background: '#f5f5f5' }}>
                        <th style={mThS2}></th>
                        <th style={mThS2}>12oz</th><th style={mThS2}>16oz</th>
                        <th style={mThS2}>16oz</th><th style={mThS2}>20oz</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catMenus.length === 0 ? (
                        <tr><td colSpan={5} style={{ textAlign: 'center', color: '#ccc', padding: 20 }}>이 카테고리에 메뉴가 없습니다</td></tr>
                      ) : catMenus.map(mp => {
                        const edits = priceEdits[mp.menuCode] || {};
                        const hasChange = Object.entries(edits).some(([k, v]) => v !== (mp.prices?.[k] || 0) && v > 0);
                        return (
                          <tr key={mp._id} style={{ borderBottom: '1px solid #f0f0f0', background: hasChange ? '#FFFDE7' : '#fff' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 500 }}>{mp.menuName}</td>
                            {M_SIZE_KEYS.map(k => {
                              const price = mp.prices?.[k];
                              if (price === undefined) return <td key={k} style={{ textAlign: 'center', color: '#ddd' }}>—</td>;
                              const edited = edits[k];
                              const changed = edited !== undefined && edited !== price && edited > 0;
                              return (
                                <td key={k} style={{ textAlign: 'center', padding: '6px' }}>
                                  <input key={`${mp.menuCode}-${k}-${resetKey}`} type="number" step="0.05" defaultValue={mFmt(price)}
                                    onBlur={e => handleEdit(mp.menuCode, k, e.target.value)}
                                    style={{
                                      width: 68, padding: '5px 6px', border: `1px solid ${changed ? '#C5A55A' : '#ddd'}`,
                                      borderRadius: 5, textAlign: 'center', fontSize: 13, fontFamily: 'inherit',
                                      background: changed ? '#F5EDD6' : '#fafafa',
                                    }} />
                                  {changed && <div style={{ fontSize: 9, color: '#C62828', textDecoration: 'line-through' }}>${mFmt(price)}</div>}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>

                {/* 변경 요약 */}
                {changeSummary.length > 0 && (
                  <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, border: '2px solid #C5A55A', background: '#FDF8F0' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#5D4037', marginBottom: 6 }}>
                      변경 요약 ({changeSummary.length}건)
                    </div>
                    {changeSummary.map((c, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12 }}>
                        <span style={{ fontWeight: 500 }}>{c.menuName} <span style={{ color: '#888' }}>{M_SIZE_SHORT[c.sizeKey]}</span></span>
                        <span>
                          <span style={{ color: '#C62828', textDecoration: 'line-through' }}>${mFmt(c.oldPrice)}</span>
                          <span style={{ color: '#C5A55A', margin: '0 4px' }}>→</span>
                          <span style={{ color: '#1B5E3B', fontWeight: 700 }}>${mFmt(c.newPrice)}</span>
                        </span>
                      </div>
                    ))}
                    <button type="button" onClick={() => { setPriceEdits({}); setResetKey(k => k + 1); }}
                      style={{ marginTop: 6, fontSize: 11, color: '#888', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                      초기화
                    </button>
                  </Box>
                )}
              </Box>
            )
          )}

          {/* 변경 상세 (비 price_change이거나 추가 메모) */}
          <TextField
            label={type === 'price_change' ? '추가 메모 (선택)' : '변경 상세 내용 *'}
            multiline minRows={2}
            value={changeDetails}
            onChange={e => setChangeDetails(e.target.value)}
            placeholder={type === 'price_change'
              ? '추가 요청 사항이 있으면 입력하세요'
              : '예: 메뉴 이름 변경, 신메뉴 추가 등 상세 내용 입력'}
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <button type="button" onClick={handleClose}
          style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
          취소
        </button>
        <button type="button" onClick={handleSubmit}
          disabled={submitting || !selectedStore || (type === 'price_change' ? changeSummary.length === 0 && !changeDetails.trim() : !changeDetails.trim())}
          style={{ padding: '8px 24px', borderRadius: 8, border: 'none', background: '#551FFF', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 700, opacity: submitting ? 0.6 : 1 }}>
          {submitting ? '등록중...' : changeSummary.length > 0 ? `요청 등록 (${changeSummary.length}건)` : '요청 등록'}
        </button>
      </DialogActions>
    </Dialog>
  );
}

const STATUSES = ['received', 'in_progress', 'customer_confirm', 'confirmed', 'updating', 'completed', 'rejected'];
const STATUS_LABELS = {
  received: '접수', in_progress: '진행중', customer_confirm: '컨펌 요청',
  confirmed: '확인', updating: '업데이트', completed: '완료', rejected: '반려',
};
const STATUS_COLORS = {
  received: '#3b82f6', in_progress: '#f59e0b', customer_confirm: '#a855f7',
  confirmed: '#06b6d4', updating: '#8b5cf6', completed: '#22c55e', rejected: '#ef4444',
};

function Requests({ user }) {
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [designers, setDesigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);

  const userId = user?.user?._id || user?._id;
  const isAdmin = user?.role === 'admin' || user?.user?.role === 'admin';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, sumRes] = await Promise.all([
        api.get(`/changerequest/getall/${userId}`),
        api.get(`/changerequest/summary/${userId}`),
      ]);
      setRequests(reqRes.data.requests || []);
      setSummary(sumRes.data || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 디자이너 배정은 admin 만 (/user/getusers 는 admin 전용 — 점주·매니저는 403)
  useEffect(() => {
    if (!isAdmin) return;
    api.get(`/user/getusers/${userId}`).then(r => {
      const all = r.data?.users || [];
      setDesigners(all.filter(u => u.role === 'designer'));
    }).catch(() => {});
  }, [userId, isAdmin]);

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  const handleStatusChange = async (id, newStatus, note) => {
    await api.patch(`/changerequest/update/${id}`, {
      status: newStatus, statusNote: note || '',
      changedByName: user?.username || user?.user?.username || 'Admin',
    });
    fetchData();
    setSelected(null);
  };

  const handleAssign = async (id, designerId, designerName) => {
    await api.patch(`/changerequest/update/${id}`, {
      assignedTo: designerId, assignedToName: designerName,
      status: 'in_progress', statusNote: `Designer assigned: ${designerName}`,
      changedByName: user?.username || 'Admin',
    });
    fetchData();
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const timeDiff = (start, end) => {
    if (!start || !end) return '-';
    const ms = new Date(end) - new Date(start);
    const h = Math.floor(ms / 3600000);
    return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d ${h % 24}h`;
  };

  const handleManualSubmit = async (data) => {
    await api.post('/changerequest/add', data);
    fetchData();
  };

  const listTitle = filter === 'all' ? 'All Requests' : STATUS_LABELS[filter];

  return (
    <Layout title="Requests" user={user}>
      <div className="requests-page">
        {/* Summary cards + 메뉴얼 입력 버튼 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div className="requests-summary-grid" style={{ flex: 1 }}>
            {STATUSES.map(s => (
              <div key={s} onClick={() => setFilter(filter === s ? 'all' : s)}
                style={{
                  background: filter === s ? STATUS_COLORS[s] : 'white',
                  color: filter === s ? 'white' : STATUS_COLORS[s],
                  borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center',
                  border: `2px solid ${STATUS_COLORS[s]}`,
                  transition: '0.15s',
                }}>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{summary[s] || 0}</div>
                <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2 }}>{STATUS_LABELS[s]}</div>
              </div>
            ))}
          </div>
          {(isAdmin || ['designer', 'user'].includes(user?.role || user?.user?.role)) && (
            <button type="button" onClick={() => setManualOpen(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'linear-gradient(135deg, #551FFF 0%, #7c3aed 100%)',
                color: '#fff', border: 'none', borderRadius: 12,
                padding: '14px 20px', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                boxShadow: '0 4px 14px rgba(85, 31, 255, 0.3)',
                transition: '0.2s',
              }}>
              <AddCircleOutlineIcon style={{ fontSize: 18 }} />
              메뉴얼 입력
            </button>
          )}
        </div>

        {/* Request list */}
        <div className="requests-list-card">
          <div className="requests-list-header">
            <div className="requests-list-title-block">
              <h2 className="requests-list-title">
                <AssignmentIcon style={{ fontSize: 22, color: '#551fff', flexShrink: 0, marginTop: 2 }} />
                <span>
                  {listTitle}
                  <span className="requests-list-sub"> ({filtered.length})</span>
                </span>
              </h2>
            </div>
            {filter !== 'all' && (
              <button type="button" onClick={() => setFilter('all')} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>
                Show All
              </button>
            )}
          </div>

          {/* Mobile: card list */}
          <div className="requests-mobile-only">
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#aaa', padding: 28 }}>No requests</div>
            ) : (
              filtered.map((r) => (
                <button type="button" key={r._id} className="request-m-card" onClick={() => setSelected(r)}>
                  <div className="request-m-card-top">
                    <p className="request-m-store">{r.store?.name || '—'}</p>
                    <span className="request-m-badge" style={{ background: STATUS_COLORS[r.status] }}>{STATUS_LABELS[r.status]}</span>
                  </div>
                  <div className="request-m-row">
                    <span className="request-m-type">{r.type}</span>
                    <SlaChip sla={r.sla} />
                    {r.menuName ? (
                      <span><span className="request-m-label">Menu</span>{r.menuName}</span>
                    ) : null}
                  </div>
                  {r.changeDetails ? (
                    <div className="request-m-details">{r.changeDetails}</div>
                  ) : null}
                  <div className="request-m-footer">
                    <span>{r.requestedBy?.username || '—'}{r.assignedToName ? ` · ${r.assignedToName}` : ''}</span>
                    <span>{r.status === 'completed' ? timeDiff(r.createdAt, r.completedAt) : fmtDate(r.createdAt)}</span>
                  </div>
                  {isAdmin && r.status !== 'completed' && r.status !== 'rejected' && (
                    <Box sx={{ mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>상태</InputLabel>
                        <Select label="상태" value={r.status}
                          onChange={(e) => handleStatusChange(r._id, e.target.value)}>
                          {STATUSES.map((s) => <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  {isAdmin && !r.assignedToName && designers.length > 0 ? (
                    <div style={{ marginTop: 10 }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <label htmlFor={`assign-${r._id}`} style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 4 }}>디자이너 배정</label>
                      <select
                        id={`assign-${r._id}`}
                        style={{ width: '100%', fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb' }}
                        defaultValue=""
                        onChange={(e) => {
                          const d = designers.find((x) => x._id === e.target.value);
                          if (d) handleAssign(r._id, d._id, d.username);
                        }}
                      >
                        <option value="" disabled>선택…</option>
                        {designers.map((d) => <option key={d._id} value={d._id}>{d.username}</option>)}
                      </select>
                    </div>
                  ) : null}
                </button>
              ))
            )}
          </div>

          <div className="requests-desktop-only requests-desktop-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={th}>Store</th>
                  <th style={th}>Type</th>
                  <th style={th}>Menu</th>
                  <th style={th}>Details</th>
                  <th style={th}>Requester</th>
                  <th style={th}>Designer</th>
                  <th style={th}>Status</th>
                  <th style={th}>SLA</th>
                  <th style={th}>Time</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={10} style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>No requests</td></tr>
                ) : filtered.map(r => (
                  <tr key={r._id} style={{ cursor: 'pointer' }} onClick={() => setSelected(r)}>
                    <td style={td}><strong>{r.store?.name || '-'}</strong></td>
                    <td style={td}><span style={{ fontSize: 11, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{r.type}</span></td>
                    <td style={td}>{r.menuName || '-'}</td>
                    <td style={{ ...td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.changeDetails}</td>
                    <td style={td}>{r.requestedBy?.username || '-'}</td>
                    <td style={td}>
                      {r.assignedToName ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <PersonIcon style={{ fontSize: 14, color: '#8b5cf6' }} />{r.assignedToName}
                        </span>
                      ) : isAdmin ? (
                        <select style={{ fontSize: 11, padding: '2px 4px', borderRadius: 4, border: '1px solid #ddd' }}
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            const d = designers.find(d => d._id === e.target.value);
                            if (d) handleAssign(r._id, d._id, d.username);
                          }} defaultValue="">
                          <option value="" disabled>Assign</option>
                          {designers.map(d => <option key={d._id} value={d._id}>{d.username}</option>)}
                        </select>
                      ) : '-'}
                    </td>
                    <td style={td}>
                      <span style={{
                        display: 'inline-block', padding: '3px 8px', borderRadius: 999,
                        fontSize: 11, fontWeight: 700, color: 'white',
                        background: STATUS_COLORS[r.status],
                      }}>{STATUS_LABELS[r.status]}</span>
                    </td>
                    <td style={td}><SlaChip sla={r.sla} /></td>
                    <td style={{ ...td, fontSize: 11, color: '#999' }}>
                      {r.status === 'completed' ? timeDiff(r.createdAt, r.completedAt) : fmtDate(r.createdAt)}
                    </td>
                    <td style={td} onClick={e => e.stopPropagation()}>
                      {isAdmin && r.status !== 'completed' && r.status !== 'rejected' && (
                        <select style={{ fontSize: 11, padding: '3px 6px', borderRadius: 4, border: '1px solid #ddd' }}
                          value={r.status}
                          onChange={e => handleStatusChange(r._id, e.target.value)}>
                          {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle style={{ borderBottom: '3px solid #551FFF', fontWeight: 700 }}>
              Request Detail — {selected.store?.name}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <div><strong>Type:</strong> {selected.type}</div>
                <div><strong>Menu:</strong> {selected.menuName || '-'}</div>
                <div><strong>Details:</strong> {selected.changeDetails}</div>
                <div><strong>Requester:</strong> {selected.requestedBy?.username} ({selected.requestedBy?.email})</div>
                <div><strong>Designer:</strong> {selected.assignedToName || 'Not assigned'}</div>
                {selected.priceChanges?.length > 0 && (
                  <div>
                    <strong>Price Changes:</strong>
                    <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                      {selected.priceChanges.map((p, i) => (
                        <li key={i}>{p.sizeKey}: ${(p.oldPrice/100).toFixed(2)} → ${(p.newPrice/100).toFixed(2)}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Status Log */}
                <div style={{ marginTop: 8 }}>
                  <strong>Status History:</strong>
                  <div style={{ marginTop: 8, borderLeft: '3px solid #e5e7eb', paddingLeft: 16 }}>
                    {(selected.statusLog || []).map((log, i) => (
                      <div key={i} style={{ marginBottom: 12, position: 'relative' }}>
                        <div style={{
                          position: 'absolute', left: -23, top: 2, width: 10, height: 10,
                          borderRadius: '50%', background: STATUS_COLORS[log.status] || '#999',
                        }} />
                        <div style={{ fontSize: 12 }}>
                          <span style={{ fontWeight: 700, color: STATUS_COLORS[log.status] }}>{STATUS_LABELS[log.status]}</span>
                          <span style={{ color: '#999', marginLeft: 8 }}>{fmtDate(log.at)}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>
                          {log.changedByName}{log.note ? ` — ${log.note}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Processing time */}
                {selected.completedAt && (
                  <div style={{ background: '#f0fdf4', padding: '8px 12px', borderRadius: 8, fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                    <CheckCircleIcon style={{ fontSize: 14, verticalAlign: 'text-bottom', marginRight: 4 }} />
                    Total processing time: {timeDiff(selected.createdAt, selected.completedAt)}
                  </div>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
      {/* Manual Request Modal */}
      <ManualRequestModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onSubmit={handleManualSubmit}
        userId={userId}
        currentUser={user}
        designers={designers}
      />
    </Layout>
  );
}

const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#92959E', fontWeight: 700, textTransform: 'uppercase', borderBottom: '2px solid #f0f0f0', whiteSpace: 'nowrap' };
const td = { padding: '10px 10px', borderBottom: '1px solid #f5f5f7', verticalAlign: 'middle' };

export default Requests;
